import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SellerPageContainer } from '@/components/seller/layout/SellerPageContainer';
import { sellerService } from '@/services/seller';
import { useToast } from '@/components/common/Toast';
import { useDiscountProducts } from '@/hooks/discount/useDiscountProducts';
import { DiscountRateGuide } from '@/components/seller/discount/DiscountRateGuide';
import { RegisteredSection } from '@/components/seller/discount/sections/RegisteredSection';
import { AvailableSection } from '@/components/seller/discount/sections/AvailableSection';
import { DiscountSaveButton } from '@/components/seller/discount/DiscountSaveButton';

export function ReservationDiscountPage() {
  const navigate = useNavigate();
  const { ToastContainer, showToast } = useToast();
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [reservationDiscountEnabled, setReservationDiscountEnabled] = useState(false);
  const [isTogglingReservation, setIsTogglingReservation] = useState(false);
  const [tomorrowHours, setTomorrowHours] = useState({
    reservationStart: '00:00',
    reservationEnd: '21:00',
    pickupStart: '10:00', 
    pickupEnd: '21:00',
    dayName: '월'
  });

  // 다음날 영업시간 정보 계산
  const calculateTomorrowOperatingHours = async (restaurantId: string) => {
    try {
      const result = await sellerService.getOperatingHours(restaurantId);
      
      if (result.success && result.data) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const dayOfWeek = tomorrow.getDay(); // 0=일요일, 1=월요일, ...
        const tomorrowHours = result.data[dayOfWeek];
        
        if (tomorrowHours && !tomorrowHours.isClosed && tomorrowHours.openTime && tomorrowHours.closeTime) {
          return {
            reservationStart: '00:00', // 자정부터
            reservationEnd: subtractHour(tomorrowHours.closeTime, 1), // 영업종료 1시간 전까지
            pickupStart: addHour(tomorrowHours.openTime, 1), // 오픈 1시간 후부터
            pickupEnd: tomorrowHours.closeTime, // 영업종료까지
            dayName: ['일', '월', '화', '수', '목', '금', '토'][dayOfWeek]
          };
        }
      }
    } catch (error) {
      console.error('Failed to calculate tomorrow operating hours:', error);
    }
    
    // 기본값 fallback
    return {
      reservationStart: '00:00',
      reservationEnd: '21:00',
      pickupStart: '10:00',
      pickupEnd: '21:00',
      dayName: '월'
    };
  };

  const addHour = (timeStr: string, hours: number): string => {
    const [hour, minute] = timeStr.split(':').map(Number);
    const newHour = hour + hours;
    return `${newHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  const subtractHour = (timeStr: string, hours: number): string => {
    const [hour, minute] = timeStr.split(':').map(Number);
    const newHour = hour - hours;
    return `${newHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  // 레스토랑 ID 가져오기 및 영업시간 계산
  useEffect(() => {
    const getRestaurantIdAndHours = async () => {
      const result = await sellerService.getSellerRestaurantId();
      if (result.success && result.restaurantId) {
        setRestaurantId(result.restaurantId);
        
        // 다음날 영업시간 기반 시간 계산
        const hours = await calculateTomorrowOperatingHours(result.restaurantId);
        setTomorrowHours(hours);

        // 예약할인 활성화 상태 조회
        const statusResult = await sellerService.getReservationDiscountStatus(result.restaurantId);
        if (statusResult.success && statusResult.data) {
          setReservationDiscountEnabled(statusResult.data.reservation_discount_enabled);
        }
      } else {
        showToast(result.error || '레스토랑 정보를 찾을 수 없습니다');
      }
    };
    getRestaurantIdAndHours();
  }, [showToast]);

  // 예약할인 토글 핸들러
  const handleReservationToggle = async (enabled: boolean) => {
    if (!restaurantId || isTogglingReservation) return;

    setIsTogglingReservation(true);
    try {
      const result = await sellerService.toggleReservationDiscount(restaurantId, enabled);
      if (result.success) {
        setReservationDiscountEnabled(enabled);
        showToast(enabled ? '예약할인이 활성화되었습니다' : '예약할인이 비활성화되었습니다');
      } else {
        showToast(result.error || '상태 변경에 실패했습니다');
      }
    } catch (error) {
      showToast('상태 변경 중 오류가 발생했습니다');
    } finally {
      setIsTogglingReservation(false);
    }
  };

  // 할인 상품 관리 훅
  const {
    products,
    registeredProducts,
    availableProducts,
    loading,
    saving,
    error,
    handleDiscountRateChange,
    handleStockChange,
    toggleProductSelection,
    saveProducts,
    selectAllRegistered,
    selectAllAvailable,
    selectedCount,
    canSave
  } = useDiscountProducts({
    restaurantId: restaurantId || '',
    offerType: 'RESERVATION_DISCOUNT'
  });

  const handleSave = async () => {
    
    const result = await saveProducts();
    if (result?.success) {
      showToast('예약할인 제품설정이 저장되었습니다');
      setTimeout(() => {
        navigate('/seller/store');
      }, 3000);
    } else {
      showToast(error || '저장에 실패했습니다');
    }
  };

  if (loading) {
    return (
      <SellerPageContainer activeNavId="store">
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">상품 목록을 불러오는 중...</p>
          </div>
        </div>
      </SellerPageContainer>
    );
  }

  return (
    <SellerPageContainer activeNavId="store">
      <div className="pb-32">
        {/* 헤더 */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/seller/store')}
            className="mr-4 p-2 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-gray-800">예약할인 상품설정</h1>
        </div>

        {/* 예약할인 시간 정보 */}
        <div className="bg-white rounded-lg p-4 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">예약할인 시간 정보</h2>
            
            {/* 예약할인 활성화 토글 */}
            <div className="flex items-center space-x-3">
              <span className={`text-sm font-medium ${reservationDiscountEnabled ? 'text-green-700' : 'text-gray-500'}`}>
                {reservationDiscountEnabled ? '활성화됨' : '비활성화됨'}
              </span>
              <button
                onClick={() => handleReservationToggle(!reservationDiscountEnabled)}
                disabled={isTogglingReservation}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  reservationDiscountEnabled ? 'bg-green-500' : 'bg-gray-300'
                } ${isTogglingReservation ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    reservationDiscountEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* 상태 안내 메시지 */}
          <div className={`p-3 rounded-lg mb-4 ${
            reservationDiscountEnabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          } border`}>
            <p className={`text-sm font-medium ${
              reservationDiscountEnabled ? 'text-green-700' : 'text-gray-600'
            }`}>
              {reservationDiscountEnabled 
                ? '✓ 구매자가 예약할인 상품을 주문할 수 있습니다' 
                : '⚠ 예약할인이 비활성화되어 구매자가 주문할 수 없습니다'}
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="font-medium text-gray-700">예약가능시간 ({tomorrowHours.dayName}요일)</h3>
              <p className="text-sm text-gray-600">
                {tomorrowHours.reservationStart} ~ {tomorrowHours.reservationEnd}
              </p>
            </div>
            
            <div className="space-y-2">
              <h3 className="font-medium text-gray-700">수령가능시간 (다음날)</h3>
              <p className="text-sm text-gray-600">
                {tomorrowHours.pickupStart} ~ {tomorrowHours.pickupEnd}
              </p>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              ※ 예약할인은 매일 자정부터 적용되며, 다음날 수령하는 상품입니다.
            </p>
          </div>
        </div>

        {/* 할인율 안내 */}
        <DiscountRateGuide type="reservation" />

        {/* 등록된 예약할인 상품 */}
        <RegisteredSection
          products={[...registeredProducts, ...products.filter(p => p.selected && !p.isRegisteredInDB)]}
          type="reservation"
          onDiscountRateChange={handleDiscountRateChange}
          onStockChange={handleStockChange}
          onProductSelect={toggleProductSelection}
          onSelectAll={selectAllRegistered}
        />

        {/* 등록 가능한 상품 목록 */}
        <AvailableSection
          products={availableProducts}
          type="reservation"
          onDiscountRateChange={handleDiscountRateChange}
          onStockChange={handleStockChange}
          onProductSelect={toggleProductSelection}
          onSelectAll={selectAllAvailable}
        />

        {/* 저장 버튼 */}
        <DiscountSaveButton
          type="reservation"
          canSave={canSave}
          saving={saving}
          selectedCount={selectedCount}
          onSave={handleSave}
          isFloating={true}
        />
      </div>

      <ToastContainer />
    </SellerPageContainer>
  );
}